import React, { useEffect, useState, useRef, useCallback } from 'react';
import { endOfDay, format, startOfMonth } from 'date-fns';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, CircularProgress, Box, Divider, Chip,
    FormControl, InputLabel, MenuItem, Select, Tooltip, SelectChangeEvent
} from '@mui/material';
import { config } from './config';
import DateRangePicker from '../../pages/CostAnalysis/DateRangePicker';
import ExcelDownloadButton from '../MasterComponent/Button/ExcelDownloadButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { exportToFile } from '../../utils/formateXLSX';

export const OperationLogTable: React.FC = () => {
    const CompanyName = useSelector((state: RootState) => state.user.userData?.CompanyName);

    const today = new Date();
    const defaultStartDate = startOfMonth(today);
    const defaultEndDate = today;

    // Date range state
    const [startDate, setStartDate] = useState<Date>(defaultStartDate);
    const [endDate, setEndDate] = useState<Date>(defaultEndDate);
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [module, setModule] = useState('');
    const [action, setAction] = useState('');
    const [cursor, setCursor] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);

    const observer = useRef<IntersectionObserver | null>(null);
    const fetchIdRef = useRef(0);

    const getActionColor = (action: string) => {
        switch (action?.toLowerCase()) {
            case 'create': return '#2e7d32';
            case 'update': return '#ed6c02';
            case 'delete': return '#d32f2f';
            case 'paused': return '#f57c00';
            case 'resumed': return '#1976d2';
            default: return '#1976d2';
        }
    };

    const handleDateChange = (start: Date | null, end: Date | null) => {
        if (start) setStartDate(start);
        if (end) setEndDate((end));
        setCursor(null);
    };

    const getQueryParams = (
        pagination: boolean = true,
        overrides?: { cursor?: string | null; logs?: any[] }
    ) => {
        const effectiveLogs = overrides?.logs ?? logs;
        const effectiveCursor = overrides && 'cursor' in overrides ? overrides.cursor : cursor;
        const filters: any = {};
        if (module) filters['entityName'] = module;
        if (action) filters['action'] = action;
        if (startDate) filters['startDate'] = format(startDate, 'yyyy-MM-dd');
        if (endDate) filters['endDate'] = format((endDate), 'yyyy-MM-dd');
        if (!pagination) {
            const exportStart = effectiveLogs[0]?.created_at; // first log visible
            const exportEnd = effectiveLogs[effectiveLogs.length - 1]?.created_at; // last log visible
            if (exportStart) filters['exportStart'] = exportStart;
            if (exportEnd) filters['exportEnd'] = exportEnd;
        }

        const params: any = {
            CompanyName,
            ...filters,
        };

        if (Object.keys(filters).length) {
            params.filters = JSON.stringify(filters);
        }

        if (pagination) {
            params.limit = 10;
            if (effectiveCursor) params.cursor = effectiveCursor;
        }
        return params;
    };


    const fetchLogs = async (append = false, overrides?: { cursor?: string | null; logs?: any[] }) => {
        if (append && (loading || !hasMore)) return;

        let fetchId = 0;
        try {
            fetchId = ++fetchIdRef.current;
            setLoading(true);
            const queryParams = getQueryParams(true, overrides);
            const res = await config.getAuditReport(queryParams);
            const result = res.data?.result;

            const newData = result?.data || [];

            if (fetchId !== fetchIdRef.current) return;

            setLogs(prev => (append ? [...prev, ...newData] : newData));
            setCursor(result?.nextCursor ?? null);
            setHasMore(result?.hasMore ?? false);
        } catch (err) {
            console.error('Error fetching logs:', err);
        } finally {
            if (fetchId && fetchId === fetchIdRef.current) {
                setLoading(false);
            }
        }
    };

    const handleDownload = async () => {
        try {
            const queryParams = getQueryParams(false);
            const res = await config.downloadAuditReport(queryParams);
            const base64Data = res?.data?.result;
            if (!base64Data) {
                console.error('No data received for CSV download');
                return;
            }
            exportToFile(base64Data, `Operation_Logs_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
        } catch (err) {
            console.error('Error downloading logs:', err);
        }
    };


    // ✅ Observer for infinite scroll
    const lastLogRef = useCallback(
        (node: HTMLTableRowElement | null) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    fetchLogs(true);
                }
            });

            if (node && hasMore) observer.current.observe(node);
        },
        [loading, hasMore] // ✅ stable dependencies
    );

    // ✅ Reset pagination on filter change
    useEffect(() => {
        if (!CompanyName) return;

        setLogs([]);
        setCursor(null);
        setHasMore(true);

        fetchLogs(false, { cursor: null, logs: [] });
    }, [module, action, startDate, endDate, CompanyName]);

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" gutterBottom>Operation Logs</Typography>


            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 1 }}>
                <ExcelDownloadButton
                    isExcelDownloading={false}
                    onDownloadCsv={handleDownload}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Module</InputLabel>
                        <Select value={module} label="Module" onChange={(e: SelectChangeEvent) => setModule(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="account">Account</MenuItem>
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="invite">Invitation</MenuItem>
                        <MenuItem value="monitoring">URL Monitoring</MenuItem>
                    </Select>
                </FormControl>

                    <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Action</InputLabel>
                        <Select value={action} label="Action" onChange={(e: SelectChangeEvent) => setAction(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="create">Create</MenuItem>
                        <MenuItem value="update">Update</MenuItem>
                        <MenuItem value="delete">Delete</MenuItem>
                    </Select>
                </FormControl>

                <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={handleDateChange}
                />
            </Box>
            <TableContainer
                className="table adminTable adminPanelTable"
            >
                <Table stickyHeader size="small" aria-label="operation log table" sx={{ maxHeight: '50vh', overflowY: 'scroll' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>S.no</TableCell>
                            <TableCell>Operation</TableCell>
                            <TableCell>Module</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Done By</TableCell>
                            <TableCell>Details</TableCell>
                            <TableCell>Created At</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {logs.map((log, index) => {
                            const isLast = index === logs.length - 1;
                            return (
                                <TableRow ref={isLast ? lastLogRef : null} key={log.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{log.operation}</TableCell>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>{log.entity_name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.action}
                                            sx={{
                                                backgroundColor: getActionColor(log.action),
                                                color: '#fff',
                                                fontWeight: 600,
                                                textTransform: 'capitalize',
                                            }}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{`${log.firstName ?? ''} ${log.lastName ?? ''}`}</TableCell>
                                    <TableCell
                                        sx={{
                                            maxWidth: '40%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                        title={JSON.stringify(log.details)}
                                    >
                                        {log?.details?.message || '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={new Date(log.created_at).toLocaleString('en-IN', { hour12: true })}>
                                            <Typography variant="body2" sx={{ cursor: 'default' }}>
                                                {new Date(log.created_at).toLocaleDateString('en-IN')}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}

                        {loading && (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: 60, // Adjust height if needed
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}

                        {!loading && logs.length === 0 && CompanyName && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    No logs found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
